import { useEffect, useRef, useState } from "react";
import type { UserSuggestion } from "../../shared/types";
import { api, ApiError } from "../api";
import { Ico } from "./shell";
import { alertDialog } from "./Dialog";
import { AtlModalOverlay, AtlModalShell, XIcon } from "./atelier";

function initialsOf(name: string): string {
  const w = name.trim().split(/\s+/).filter(Boolean);
  if (w.length >= 2) return (w[0][0] + w[1][0]).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase() || "??";
}

export default function ShareModal({ quizId, quizTitle, onClose }: { quizId: string; quizTitle: string; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<UserSuggestion[]>([]);
  const [recipients, setRecipients] = useState<UserSuggestion[]>([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    api.listQuizShares(quizId).then((r) => setRecipients(r.users)).catch(() => {});
  }, [quizId]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 1) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(() => {
      api.searchUsers(q).then((r) => setResults(r.users)).catch(() => setResults([]));
    }, 200);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [q]);

  const sharedIds = new Set(recipients.map((r) => r.id));

  async function add(u: UserSuggestion) {
    if (busy || sharedIds.has(u.id)) return;
    setBusy(true);
    try {
      await api.shareQuiz(quizId, u.id);
      setRecipients((cur) => [...cur, u]);
      setQ("");
      setResults([]);
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Could not share this quiz");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(u: UserSuggestion) {
    try {
      await api.unshareQuiz(quizId, u.id);
      setRecipients((cur) => cur.filter((r) => r.id !== u.id));
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Could not remove access");
    }
  }

  return (
    <AtlModalOverlay onClose={onClose}>
      <AtlModalShell
        icon={<Ico name="team" size={20} />}
        iconBg="var(--atl-gold-soft)"
        iconFg="var(--atl-gold)"
        title="Share quiz"
        sub={quizTitle}
        onClose={onClose}
        max={460}
      >
        <div style={{ marginTop: 16, position: "relative" }}>
          <div className="atl-eyebrow" style={{ marginBottom: 8 }}>ADD PEOPLE BY NAME</div>
          <input className="atl-tf" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Start typing a name…" autoFocus />
          {results.length > 0 && (
            <div className="atl-card" style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 6, padding: 6, zIndex: 2, maxHeight: 240, overflowY: "auto", boxShadow: "0 18px 44px rgba(40,25,15,.18)" }}>
              {results.map((u) => {
                const already = sharedIds.has(u.id);
                return (
                  <div key={u.id} onClick={() => add(u)} style={{ display: "flex", alignItems: "center", gap: 11, padding: "8px 10px", borderRadius: 10, opacity: already ? 0.5 : 1, cursor: already ? "default" : "pointer" }}>
                    <span style={{ width: 32, height: 32, borderRadius: 9, background: "var(--atl-gold)", color: "#fff", display: "grid", placeItems: "center", fontWeight: 700, fontSize: 12, flex: "0 0 auto" }}>{initialsOf(u.displayName)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5 }}>{u.displayName}</div>
                      <div style={{ fontSize: 12, color: "var(--atl-faint)", fontWeight: 500 }}>@{u.username}</div>
                    </div>
                    {already && <span style={{ fontSize: 12, color: "var(--atl-faint)", fontWeight: 600 }}>Added</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ marginTop: 18 }}>
          <div className="atl-eyebrow" style={{ marginBottom: 10 }}>SHARED WITH {recipients.length > 0 ? `(${recipients.length})` : ""}</div>
          {recipients.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--atl-faint)", fontWeight: 500 }}>Not shared with anyone yet.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {recipients.map((u) => (
                <div key={u.id} className="atl-row" style={{ padding: "9px 13px" }}>
                  <span style={{ width: 32, height: 32, borderRadius: 9, background: "var(--atl-gold)", color: "#fff", display: "grid", placeItems: "center", fontWeight: 700, fontSize: 12, flex: "0 0 auto" }}>{initialsOf(u.displayName)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>{u.displayName}</div>
                    <div style={{ fontSize: 12, color: "var(--atl-faint)", fontWeight: 500 }}>@{u.username}</div>
                  </div>
                  <button className="atl-iconbtn atl-iconbtn--danger" title="Remove access" onClick={() => revoke(u)}><XIcon size={13} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </AtlModalShell>
    </AtlModalOverlay>
  );
}
